








































































































































  import moment from "moment";
  import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
  import { getModule } from "vuex-module-decorators";
  import { Chart } from "highcharts-vue";
  import HighchartsNoData from "highcharts/modules/no-data-to-display";
  import Highcharts from "highcharts";
  import { VesselComparisonMode } from "@/types/vesselComparison";
  import { Header } from "@/types/VDataTable";
  import { ILongTrendPeriod } from "@/types/longTrendPeriod";
  import { LongTrendPeriodMeta } from "@/types/longTrendPeriodMeta";
  import { Vessel } from "@/types/vessel";
  import store from "@/store";
  import VesselComparison from "@/store/clients/VesselComparison.module";
  import FoulingModule from "@/store/clients/Diagnostics.module";
  import ComparisonActions from "./ComparisonActions.vue";
  import DataCell from "@/components/FleetOverview/FleetOverviewTable/DataCell.vue";
  import { tableHeaders } from "./tableHeaders";
  import { ITrendBenefit } from "@/types/trendBenefit";
  import HullCoatingModal from "@/components/diagnostics/modals/HullCoatingModal.vue";
import { HullCoating } from "@/types/HullCoating";

  type TableRow = Record<string, string | number>;

  HighchartsNoData(Highcharts);
  
  const VesselComparisonData = getModule(VesselComparison, store);
  const Fouling = getModule(FoulingModule, store);

  const toMs = (d: string | Date) => new Date(d).getTime();
  const toNum = (v: any) => typeof v === 'number' ? v : Number(v);
  const toCommonName = (trendName: string) => {
    const lower = trendName.toLocaleLowerCase();

    if (lower.includes("speed")) return "shipSpeed";
    if (lower.includes("rpm")) return "rpm";
    if (lower.includes("sfr")) return "sfr";

    return trendName;
  }

  @Component({
    components: {
      ComparisonActions,
      Highcharts: Chart,
      DataCell,
      HullCoatingModal,
    },
  })
  export default class ComparisonCard extends Vue {
    @Prop() vesselIds!: number[];
    @Prop() vessels!: Vessel[];
    @Prop() mode!: VesselComparisonMode;
    @Prop() trendName!: string;
    @Prop() trendTitle!: string;
    @Prop() longTrendId!: number;
    @Ref("HullCoatingModal") HullCoatingModal!: any;
    showError = false;
    longTrendPeriods: ILongTrendPeriod[] = [];
    benchmark = 0;
    isBenefitLoading = false;
    benefitData: ITrendBenefit = {} as ITrendBenefit;
    loadBenefitError: string | boolean = false;
    tableRows: TableRow[] = [];
    selectedVesselId: number | null = null;
    hullCoatingModalActive = false;
    hullCoatingList: HullCoating[] = [];

    get minX() {
      return Math.min(...this.longTrendPeriods.map(p => toMs(p.start)));
    }

    get maxX() {
      return Math.max(...this.longTrendPeriods.map(p => toMs(p.end)));
    }

    get comparisonTitle() {
      return `${this.trendTitle} (Last trend period)`;
    }

    get tableHeaders() {
      return tableHeaders[toCommonName(this.trendName)];
    }

    get chartOptions() {
      if (!this.longTrendPeriods?.length) return {};

      const options = {
        time: {
          useUTC: false,
        },
        chart: {
          type: "line",
          zoomType: "x",
          spacingRight: 20,
          animation: false,
          height: 436,
          spacingTop: 40,
          backgroundColor: this.themeColor("BackgroundPrimaryDefault"),
          style: { fontFamily: "Helvetica Neue" },
        },
        credits: {
          enabled: false,
        },
        navigation: {
          menuStyle: {
            color: this.themeColor("ContentPrimary"),
            backgroundColor: this.themeColor("BackgroundElevatedPrimaryDefault"),
            borderColor: this.themeColor("BorderBrandTertiary"),
            boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 0px 2px 0px rgba(0, 0, 0, 0.08)",
            borderRadius: "8px",
            display: "flex",
            flex: "1 0 0",
            flexDirection: "column",
            alignItems: "flex-start",
            alignSelf: "stretch",
          },
          menuItemStyle: {
            color: this.themeColor("ContentPrimary"),
            fontSize: "14px",
            fontWeight: "400",
            display: "flex",
            alignItems: "center",
            alignSelf: "stretch",
            padding: "8px",
            margin: "2px 6px",
            borderRadius: "6px",
          },
          menuItemHoverStyle: {
            background: this.themeColor("BackgroundBrandTertiaryPressed"),
            color: this.themeColor("ContentPrimary"),
          },
        },
        legend: {
          enabled: true,
          itemStyle: {
            fontSize: "14px",
            color: this.themeColor("ContentTertiary"),
            fontWeight: "400",
          },
        },
        title: {
          text: this.comparisonTitle,
          align: 'left',
          y: 0,
          style: {
            color: this.themeColor('ContentPrimary'),
            fontSize: '20px',
            fontWeight: '500',
          },
        },
        yAxis: {
          title: {
            text: "",
          },
          min: -50,
          max: 50,
          labels: {
            format: "{value}%",
            style: { color: this.themeColor("ContentPrimary") },
          },
          gridLineColor: this.themeColor("ContentInverseSecondary"),
        },
        xAxis: {
          type: 'datetime',
          tickPixelInterval: 120,
          min: this.minX,
          max: this.maxX,
          dateTimeLabelFormats: {
            day: '%e %b',
            week: '%e %b',
            month: '%b %Y',
            year: '%Y',
          },
          labels: {
            style: {
              color: this.themeColor("ContentPrimary"),
              fontSize: "12px",
            },
          },
          lineColor: this.themeColor("ContentInverseSecondary"),
          tickColor: this.themeColor("ContentInverseSecondary"),
        },
        series: this.dataSeries,
        tooltip: {
          backgroundColor: this.themeColor("BackgroundTooltip"),
          borderWidth: 0,
          borderRadius: 16,
          padding: 12,
          shadow: false,
          style: {
            color: "#EBEBEB",
          },
        },
        plotOptions: {
          series: {
            zIndex: 1,
          },
        },
        exporting: {
          filename: `description_long`,
          chartOptions: {
            legend: {
              enabled: false,
            },
            title: {
              text: `description_long`,
              style: {
                width: "450px",
              },
            },
          },
          buttons: {
            contextButton: {
              borderRadius: 8,
              x: 20,
              y: -40,
            },
          },
        },
      };

      return options;
    }

    get dataSeries() {
      const series: Highcharts.SeriesOptionsType[] = [];
      const firstLongTrendPeriod = this.longTrendPeriods[0];

      series.push({
        name: "Benchmark",
        type: "line",
        dashStyle: "Dash",
        color: this.themeColor("ContentInfoDefault") as string,
        zIndex: 4,
        visible: this.benchmark !== 0,
        tooltip: {
          valueDecimals: 1,
          valueSuffix: "%",
        },
        data: [
          [new Date(this.formatDatetimeToUTC(firstLongTrendPeriod?.start)).getTime(), this.benchmark],
          [new Date().getTime(), this.benchmark],
        ]
      });

      for (const p of this.longTrendPeriods) {
        const deviationData = (p.longTrendData ?? [])
          .map(pt => [toMs(pt.x), toNum(pt.y)] as [number, number])
          .sort((a, b) => a[0] - b[0]);
        const vessel = this.vessels.find(v => v.id === p.vesselId);

        series.push({
          type: 'line',
          name: `${vessel!.name} (Deviation from baseline)`,
          data: deviationData,
          tooltip: { valueSuffix: p.unitCaption ?? '%' },
          marker: { enabled: false },
          lineWidth: 1.5,
          zIndex: 2,
          visible: false,
        } as Highcharts.SeriesLineOptions);

        if (p.trendline?.start && p.trendline?.end) {
          const tl = [
            [toMs(p.trendline.start.x), toNum(p.trendline.start.y)],
            [toMs(p.trendline.end.x), toNum(p.trendline.end.y)],
          ] as [number, number][];

          series.push({
            type: 'line',
            name: `${vessel!.name} (Trend)`,
            data: tl,
            enableMouseTracking: false,
            marker: { enabled: false },
            lineWidth: 2,
            zIndex: 3,
          } as Highcharts.SeriesLineOptions);
        }
      }

      return series;
    }

    mounted() {
      this.longTrendPeriods = this.vesselIds.map(vesselId => {
        const trendId = VesselComparisonData.trendIdFor(vesselId, this.trendName);
        if (!trendId) return undefined;

        return VesselComparisonData.lastPeriodFor(trendId);
      }).filter((trend): trend is ILongTrendPeriod => trend !== undefined);
      this.fetchTableData();
    }
    
    themeColor(color: string) {
      const theme = this.$vuetify.theme.dark ? "dark" : "light";

      return this.$vuetify.theme.themes[theme][color];
    }

    formatDateForDisplay(date: any): string {
      return moment.utc(date).format("DD.MMM YYYY");
    }

    getLongTrendPeriodMeta(longTrendPeriod: ILongTrendPeriod): Partial<LongTrendPeriodMeta> {
      return {
        longTrendId: longTrendPeriod.longTrendId,
        longTrendName: this.trendName,
        vesselId: longTrendPeriod.vesselId,
        startDate: moment.utc(longTrendPeriod.start).toISOString(),
        endDate: moment.utc(longTrendPeriod.end).toISOString(),
      };
    }
    
    formatDatetimeToUTC(datetime: string): string {
      const [firstPart] = datetime.split("+");
      return firstPart.endsWith("z") || firstPart.endsWith("Z") ? firstPart : firstPart + "Z";
    }

    headerStyles(header: Header) {
      return {
        width: header.width ? header.width : "auto",
        "min-width": header.width ? header.width : "auto",
      };
    }

    cellClass(header: Header) {
      return ["name", "hull"].includes(header.value) ? "text-m-medium" : "text-m-regular ContentTertiary--text";
    }

    setTableData() {
      this.tableRows = this.vessels.map((vessel) => {
        const trendId = VesselComparisonData.trendIdFor(vessel.id, this.trendName);
        const results = VesselComparisonData.resultsFor(trendId as number);
        const lastTrendEvent = VesselComparisonData.lastTrendEventFor(vessel.id);
        const trend = VesselComparisonData.trendFor(vessel.id, this.trendName);
        const trendPeriod = this.longTrendPeriods.find(p => p.vesselId === vessel.id);
        const trendName = toCommonName(this.trendName);
        const trendLine = trendPeriod?.trendline;
        const benchmarkPoint = trendPeriod?.benchmarkPoint;
        const lastTrendEventTimestamp = lastTrendEvent?.timestamp
          ? moment.utc(lastTrendEvent.timestamp).format("DD, MMM, YYYY")
          : "N/A";

        const baseData: Record<string, string | number> = {
          name: vessel.name,
          id: vessel.id,
          imoNumber: vessel.imoNumber,
          lastTrendEvent: lastTrendEventTimestamp,
          change: trendLine ? (trendLine.coefficient * 365).toFixed(2) + "%" : "N/A",
          benchmark: benchmarkPoint ? benchmarkPoint.y.toFixed(2) + "%" : "N/A",
          deviation: trendLine && benchmarkPoint ? (trendLine.end.y - benchmarkPoint.y).toFixed(2) + "%" : "N/A",
          status: this.getStatusIcon(trend?.performanceStatus ?? ""),
        }

        if (trendName === "shipSpeed") {
          const speedChange = results.find(r => r.headerName.toLowerCase().includes("speed change"));
          const foi = results.find(r => r.headerName.toLowerCase().includes("fuel oil impact"));
          const hullCoating = this.hullCoatingList.find(hc => hc.vesselId === vessel.id);

          baseData.hull = "";
          baseData.speedChange = speedChange ? speedChange.displayValue : "N/A";
          baseData.foi = foi ? foi.displayValue : "N/A";
          baseData.hull = hullCoating ? `${hullCoating.manufacturerName} | ${hullCoating.typeName} | ${hullCoating.brand}` : "";
        }

        if (trendName === "rpm") {
          const rpmChange = results.find(r => r.headerName.toLowerCase().includes("rpm change"));

          baseData.rpmChange = rpmChange ? rpmChange.displayValue : "N/A";
        }

        if (trendName === "sfr") {
          const sfrChange = results.find(r => r.headerName.toLowerCase().includes("sfr change"));
          const foi = results.find(r => r.headerName.toLowerCase().includes("fuel oil impact"));

          baseData.sfrChange = sfrChange ? sfrChange.displayValue : "N/A";
          baseData.foi = foi ? foi.displayValue : "N/A";
        }

        return baseData;
      });
    }

    async fetchTableData() {
      this.isBenefitLoading = true;
      await VesselComparisonData.ensureBenefitDataForSelection({ vesselIds: this.vesselIds, trendName: this.trendName });
      const trendName = toCommonName(this.trendName);

      if (trendName === "shipSpeed") {
        await this.fetchHullCoatingData();
      }
      
      this.isBenefitLoading = false;
      this.setTableData();
    }

    async fetchHullCoatingData() {
      const promises = this.vessels.map(vessel => Fouling.fetchHullCoatingVessel(vessel.id));
      const results = await Promise.all(promises);
      this.hullCoatingList = results.filter(Boolean);
    }

    getStatusIcon(status: string): string {
      if (!status) return "mdi-close-circle-outline";
      if (status === "Ok") return "$StatusOk";
      if (status === "Observe") return "$StatusObserve";
      if (status === "NotOk") return "$StatusNotOk";
      if (status === "BenchMarking") return "$StatusBenchmarking";

      return "mdi-close-circle-outline";
    }

    setHullCoating(vesselId: number) {
      this.selectedVesselId = vesselId;
      this.HullCoatingModal.open();
    }
  }
